import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getInstitute,
  createInstitute,
  updateInstitute,
  deleteInstitute,
  addUnitToInstitute,
  removeUnitFromInstitute,
  addTutorToInstitute,
  removeTutorFromInstitute,
  addManagerToInstitute,
  removeManagerFromInstitute,
  addStudentToInstitute,
  removeStudentFromInstitute,
  listInstituteByUser,
} from '@/services/institute.service'
import { getUsersByInstituteId } from '@/services/user.service'
import type { InstituteCreatePayload, InstituteUpdatePayload, UserRole } from '@/types'
import { executeIfArgsNotNull } from '@/utils/promiseHelper'

const INSTITUTES_KEY = ['institutes'] as const

/** React Query hook — fetches all institutes via `listInstitutes`. */
export function useInstitutes(role?: UserRole, userId?: string) {
  return useQuery({
    queryKey: [...INSTITUTES_KEY, role, userId],
    queryFn: () => executeIfArgsNotNull(listInstituteByUser, [], role, userId),
  })
}

/** React Query hook — fetches a single institute by ID via `getInstitute`. */
export function useInstitute(id: string) {
  return useQuery({
    queryKey: [...INSTITUTES_KEY, id],
    queryFn: () => getInstitute(id),
    enabled: Boolean(id),
  })
}

/** React Query mutation — creates an institute via `createInstitute` and invalidates the list cache. */
export function useCreateInstitute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: InstituteCreatePayload) => createInstitute(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: INSTITUTES_KEY }),
  })
}

/** React Query mutation — updates an institute via `updateInstitute` and invalidates caches. */
export function useUpdateInstitute(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: InstituteUpdatePayload) => updateInstitute(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, id] })
    },
  })
}

/** React Query mutation — deletes an institute via `deleteInstitute` and invalidates the list cache. */
export function useDeleteInstitute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteInstitute(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: INSTITUTES_KEY }),
  })
}

/**
 * Performs useAddUnitToInstitute operation.
 * Institute is the single source of truth — only writes to institute.unitIds.
 */
export function useAddUnitToInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (unitId: string) => {
      await addUnitToInstitute(instituteId, unitId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

/**
 * Performs useRemoveUnitFromInstitute operation.
 * Institute is the single source of truth — only writes to institute.unitIds.
 */
export function useRemoveUnitFromInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (unitId: string) => {
      await removeUnitFromInstitute(instituteId, unitId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

// ── User (members) hooks ───────────────────────────────────────────────────────

/** React Query hook — fetches users belonging to an institute via `getUsersByInstituteId`. */
export function useInstituteUsers(instituteId: string) {
  return useQuery({
    queryKey: [...INSTITUTES_KEY, 'users', instituteId],
    queryFn: () => getUsersByInstituteId(instituteId),
    enabled: Boolean(instituteId),
  })
}

/** React Query mutation — adds a tutor to the institute via `addTutorToInstitute`. */
export function useAddTutorToInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tutorId: string) => addTutorToInstitute(instituteId, tutorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}

/** React Query mutation — removes a tutor from the institute via `removeTutorFromInstitute`. */
export function useRemoveTutorFromInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tutorId: string) => removeTutorFromInstitute(instituteId, tutorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}

/** React Query mutation — adds a manager to the institute via `addManagerToInstitute`. */
export function useAddManagerToInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (managerId: string) => addManagerToInstitute(instituteId, managerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}

/** React Query mutation — removes a manager from the institute via `removeManagerFromInstitute`. */
export function useRemoveManagerFromInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (managerId: string) => removeManagerFromInstitute(instituteId, managerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}

/** React Query mutation — adds a student to the institute via `addStudentToInstitute`. */
export function useAddStudentToInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (studentId: string) => addStudentToInstitute(instituteId, studentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}

/** React Query mutation — removes a student from the institute via `removeStudentFromInstitute`. */
export function useRemoveStudentFromInstitute(instituteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (studentId: string) => removeStudentFromInstitute(instituteId, studentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTITUTES_KEY })
      qc.invalidateQueries({ queryKey: [...INSTITUTES_KEY, 'users', instituteId] })
    },
  })
}
